/**
 * The big push-to-talk button.
 *
 * Hold-to-talk only — there is no toggle/latch mode. The button drives a
 * single pair of callbacks (`onPress`, `onRelease`) that fire exactly once
 * per press, regardless of which input device generated it:
 *
 * - **Pointer** (mouse, touch, pen): `pointerdown` → press,
 *   `pointerup` / `pointercancel` / `pointerleave` → release.
 * - **Keyboard** (desktop): `Space` keydown → press, `Space` keyup →
 *   release. Auto-repeat (`event.repeat`) is suppressed so a held key
 *   only fires once.
 *
 * The intersection of these rules is "any source releases the floor" —
 * lifting a finger, releasing the spacebar, the system grabbing focus,
 * or the touch sliding off the button. That intentional belt-and-braces
 * approach prevents the floor from being latched accidentally.
 */

/** Visual states the button can be in. */
export type PttButtonState = "idle" | "pressing" | "granted" | "denied";

/** Constructor options for [`createPttButton`]. */
export interface PttButtonOptions {
    /** Container the button is appended into. */
    container: HTMLElement;
    /** Fired exactly once when any input source begins a press. */
    onPress: () => void;
    /** Fired exactly once when the press ends, by any source. */
    onRelease: () => void;
}

/** Handle returned by [`createPttButton`] for state updates and teardown. */
export interface PttButtonHandle {
    /**
     * Update the visual state. The button's pressed/unpressed *logical*
     * state is driven by user input; the visual state additionally
     * reflects whether the floor was granted (green) or denied (red).
     */
    setState(state: PttButtonState): void;
    /**
     * Update the mic-level meter. Pass an RMS amplitude in `[0, 1]`;
     * the bar is mapped through a soft compander to the visible range
     * since speech RMS lives in roughly the bottom 10% of the scale.
     */
    setLevel(rms: number): void;
    /** Remove the button from the DOM and detach all listeners. */
    destroy(): void;
}

/**
 * Create the PTT button, attach all input handlers, and return a handle.
 *
 * The button uses `aria-pressed` to expose its hold state to assistive
 * tech. Keyboard handlers are attached at the document level (not the
 * button) so the spacebar works regardless of focus — appropriate for a
 * single-purpose page.
 */
export function createPttButton(opts: PttButtonOptions): PttButtonHandle {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "ptt-button";
    button.dataset.state = "idle";
    button.setAttribute("aria-pressed", "false");
    opts.container.appendChild(button);

    // Inner label and level meter. The label sits above an inset bar
    // whose width tracks recent mic level; the bar is drawn as a child
    // element rather than via a CSS gradient so we can update only the
    // width on every frame without touching the box's other styles.
    const label = document.createElement("span");
    label.className = "ptt-label";
    label.textContent = "Hold to talk";
    button.appendChild(label);

    const meter = document.createElement("span");
    meter.className = "ptt-meter";
    meter.setAttribute("aria-hidden", "true");
    // Password managers (Bitwarden, 1Password, LastPass) heuristically
    // treat small bar elements next to a button as password-strength
    // meters and inject their icon. Opt out explicitly.
    meter.setAttribute("data-bwignore", "true");
    meter.setAttribute("data-1p-ignore", "true");
    meter.setAttribute("data-lpignore", "true");
    const meterFill = document.createElement("span");
    meterFill.className = "ptt-meter-fill";
    meterFill.style.width = "0%";
    meter.appendChild(meterFill);
    button.appendChild(meter);

    /**
     * Single boolean of truth for "is some input source currently
     * pressing". Press and release are *only* fired on edges of this
     * boolean, so the upstream protocol calls (`request_floor` /
     * `release_floor`) are guaranteed to be balanced.
     */
    let pressed = false;

    const startPress = (): void => {
        if (pressed) return;
        pressed = true;
        button.setAttribute("aria-pressed", "true");
        opts.onPress();
    };

    const endPress = (): void => {
        if (!pressed) return;
        pressed = false;
        button.setAttribute("aria-pressed", "false");
        opts.onRelease();
    };

    // Pointer events: covers mouse, touch, and pen with one handler set.
    const onPointerDown = (ev: PointerEvent): void => {
        // Ignore right-click and middle-click on the desktop.
        if (ev.button !== 0) return;
        ev.preventDefault();
        // Capture so a finger that drags off the button still fires
        // `pointerup` / `pointercancel` here rather than wandering off
        // and leaving us latched.
        button.setPointerCapture(ev.pointerId);
        startPress();
    };
    const onPointerEnd = (ev: PointerEvent): void => {
        if (button.hasPointerCapture(ev.pointerId)) {
            button.releasePointerCapture(ev.pointerId);
        }
        endPress();
    };

    button.addEventListener("pointerdown", onPointerDown);
    button.addEventListener("pointerup", onPointerEnd);
    button.addEventListener("pointercancel", onPointerEnd);
    button.addEventListener("pointerleave", onPointerEnd);

    // Suppress the iOS / Android long-press context menu explicitly.
    const onContextMenu = (ev: Event): void => {
        ev.preventDefault();
    };
    button.addEventListener("contextmenu", onContextMenu);

    // Keyboard: Space anywhere on the page triggers PTT.
    const isSpace = (ev: KeyboardEvent): boolean =>
        ev.code === "Space" || ev.key === " ";

    const onKeyDown = (ev: KeyboardEvent): void => {
        if (!isSpace(ev)) return;
        // Ignore auto-repeat — a held key would otherwise fire startPress
        // every ~30ms, but we already handle "hold" via the boolean above.
        if (ev.repeat) {
            ev.preventDefault();
            return;
        }
        // Also ignore Space when typing into an input or textarea.
        const target = ev.target;
        if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            (target instanceof HTMLElement && target.isContentEditable)
        ) {
            return;
        }
        ev.preventDefault();
        startPress();
    };
    const onKeyUp = (ev: KeyboardEvent): void => {
        if (!isSpace(ev)) return;
        ev.preventDefault();
        endPress();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);

    // If the page loses focus while the user is holding (alt-tab,
    // notification, system sheet) treat it as a release. Better a
    // false-stop than a stuck mic.
    const onWindowBlur = (): void => endPress();
    window.addEventListener("blur", onWindowBlur);

    return {
        setState(state: PttButtonState): void {
            button.dataset.state = state;
            // Label tracks state so screen readers and quick visual
            // glances both reflect what the floor server has said.
            switch (state) {
                case "idle":
                    label.textContent = "Hold to talk";
                    break;
                case "pressing":
                    label.textContent = "Requesting…";
                    break;
                case "granted":
                    label.textContent = "Talking";
                    break;
                case "denied":
                    label.textContent = "Channel full";
                    break;
            }
        },
        setLevel(rms: number): void {
            // Speech RMS sits in roughly [0.005, 0.2]. Map through a
            // square-root compander so quiet speech reads visibly and
            // loud speech does not peg the bar — feels closer to a
            // dB-scaled meter without doing the log math per frame.
            const clamped = rms > 1 ? 1 : rms < 0 ? 0 : rms;
            const visible = Math.sqrt(clamped) * 100; // ~0..100
            const pct = visible > 100 ? 100 : visible;
            meterFill.style.width = `${pct.toFixed(1)}%`;
        },
        destroy(): void {
            button.removeEventListener("pointerdown", onPointerDown);
            button.removeEventListener("pointerup", onPointerEnd);
            button.removeEventListener("pointercancel", onPointerEnd);
            button.removeEventListener("pointerleave", onPointerEnd);
            button.removeEventListener("contextmenu", onContextMenu);
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("blur", onWindowBlur);
            button.remove();
        },
    };
}
